// Azure exit WS9: staff card actions on Order Details. Shows nothing until the door says the
// card client is live, so it stays hidden on production today.
//
// The card form never sends a card to CPX: Accept.js swaps it for a token in the browser, and
// the fields are cleared straight after. Each click keeps one request key across a confirm,
// so a double click or a retry cannot act twice.
import React, { useEffect, useRef, useState } from 'react';
import { sessionHeaders } from '../services/auth';

const DOOR = '/.netlify/functions/admin-write';
const ACCEPT_JS = 'https://jstest.authorize.net/v1/Accept.js';

async function door(action, body = {}) {
  const res = await fetch(DOOR, { method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() }, body: JSON.stringify({ action, ...body }) });
  const data = await res.json().catch(() => ({}));
  return res.ok ? data : { ok: false, status: res.status, message: data.error || 'Could not save. Check the order before trying again.' };
}

const money = (v) => Math.abs(Number(v) || 0).toFixed(2);
const gatewayId = (id) => String(id || '').split('#')[0];
const isGatewayId = (id) => /^[0-9]+$/.test(gatewayId(id));

function loadAccept() {
  if (window.Accept) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = ACCEPT_JS;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Could not load the card form.'));
    document.body.appendChild(s);
  });
}

function tokenize(keys, card) {
  return new Promise((resolve, reject) => {
    window.Accept.dispatchData({ authData: { clientKey: keys.clientKey, apiLoginID: keys.apiLoginID }, cardData: card }, (r) => {
      if (r.messages && r.messages.resultCode === 'Ok') resolve(r.opaqueData);
      else reject(new Error((r.messages && r.messages.message && r.messages.message[0] && r.messages.message[0].text) || 'Card not accepted.'));
    });
  });
}

export default function CardActions({ orderId, customerId, balance, payments, onChanged }) {
  const [live, setLive] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState(null);
  const [selected, setSelected] = useState('');
  const [txn, setTxn] = useState('');
  const [amount, setAmount] = useState('');
  const [openRows, setOpenRows] = useState([]);
  const [manual, setManual] = useState({ transactionId: '', amount: '', transactionType: 'Credit', last4: '' });
  const [gift, setGift] = useState({ code: '', amount: '' });
  const number = useRef(null);
  const expiry = useRef(null);
  const code = useRef(null);
  const refused = useRef(false);

  const loadCards = async () => {
    const res = await fetch('/.netlify/functions/cim-proxy', { method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() }, body: JSON.stringify({ customerId }) });
    const data = await res.json().catch(() => ({}));
    setCards((data.profile && data.profile.paymentProfiles) ? data.profile : { paymentProfiles: [] });
  };
  const loadOpen = async () => {
    const r = await door('card_actions_list');
    if (r.rows) setOpenRows(r.rows.filter((x) => String(x.order_id) === String(orderId)));
  };

  useEffect(() => {
    let gone = false;
    door('card_features').then((r) => { if (!gone && r.live) setLive(true); });
    return () => { gone = true; };
  }, []);
  useEffect(() => {
    if (!live) return;
    loadCards();
    loadOpen();
  }, [live]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!live) return null;

  const run = async (action, body, confirmText) => {
    if (busy || !window.confirm(confirmText)) return;
    setBusy(true);
    setMessage('');
    const requestKey = crypto.randomUUID();
    let r = await door(action, { ...body, requestKey });
    if (r.needsConfirm || r.reason === 'repeat') {
      if (window.confirm(`${r.message} Send it anyway?`)) r = await door(action, { ...body, requestKey: crypto.randomUUID(), allowRepeat: r.reason === 'repeat', confirmOverBalance: !!r.needsConfirm });
    }
    setBusy(false);
    setMessage(r.message || (r.ok ? 'Done.' : 'Not done.'));
    await loadOpen();
    if (r.ok && onChanged) onChanged();
  };

  const capture = (payments || []).filter((p) => p.paymentType === 'Approved' && isGatewayId(p.transactionId));
  const pick = cards && cards.paymentProfiles.find((c) => c.customerPaymentProfileId === selected);

  const addCard = async () => {
    if (busy || refused.current) return;
    refused.current = true;
    setBusy(true);
    setMessage('');
    const cardData = { cardNumber: number.current.value.replace(/[^0-9]/g, ''), month: expiry.current.value.slice(0, 2), year: expiry.current.value.slice(-2), cardCode: code.current.value };
    number.current.value = '';
    expiry.current.value = '';
    code.current.value = '';
    try {
      const keys = await door('card_accept_keys');
      if (!keys.clientKey) throw new Error(keys.message || 'Card form not available.');
      await loadAccept();
      const opaqueData = await tokenize(keys, cardData);
      const r = await door('card_add', { requestKey: crypto.randomUUID(), customerId, customerProfileId: cards.customerProfileId, opaqueData, orderId, billTo: {} });
      setMessage(r.ok ? 'Card added.' : (r.message || 'Card not added.'));
      if (r.ok) await loadCards();
    } catch (e) {
      setMessage(e.message);
    }
    refused.current = false;
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--gray-200)', paddingTop: 12, fontSize: 12 }}>
      {openRows.filter((r) => r.status === 'approved' || r.status === 'declined').map((r) => (
        <div key={r.id} style={{ marginBottom: 6 }}>Still to send for this {r.action}: {(r.owed || []).map((o) => o.kind).join(', ')}</div>
      ))}
      {openRows.filter((r) => r.status === 'started' || r.status === 'unknown').map((r) => (
        <div key={r.id} style={{ color: 'var(--red)', marginBottom: 6 }}>
          No clear answer on a {r.action} by {r.actor}. Check Authorize.Net.{' '}
          <button className="btn btn-outline" onClick={() => {
            const note = window.prompt('What did Authorize.Net show?');
            if (!note) return;
            const moved = window.confirm('Did money move? OK for yes, Cancel for no.');
            door('card_action_resolve', { id: r.id, note, moneyMoved: moved }).then((x) => { setMessage(x.ok ? (x.recordPayment ? 'Closed. Enter the money below.' : 'Closed.') : x.message); loadOpen(); });
          }}>Close this</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <label className="form-label" htmlFor="ca-txn">Transaction</label>
        <select id="ca-txn" className="form-input" style={{ width: 180 }} value={txn} onChange={(e) => setTxn(e.target.value)}>
          <option value="">Choose</option>
          {capture.map((p) => <option key={p.transactionId} value={gatewayId(p.transactionId)}>{gatewayId(p.transactionId)} ${money(p.amount)}</option>)}
        </select>
        <button className="btn btn-outline" disabled={busy || !txn} onClick={() => run('card_void', { transactionId: txn, orderId }, `Void ${txn}?`)}>Void</button>
        <label className="form-label" htmlFor="ca-amount">Amount</label>
        <input id="ca-amount" className="form-input" style={{ width: 90 }} value={amount} placeholder={money(balance)} onChange={(e) => setAmount(e.target.value)} />
        <button className="btn btn-outline" disabled={busy || !txn} onClick={() => run('card_refund', { orderId, refTransId: txn, amount: money(amount) }, `Refund $${money(amount)} to ${txn}?`)}>Refund</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <label className="form-label" htmlFor="ca-card">Card</label>
        <select id="ca-card" className="form-input" style={{ width: 220 }} value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Choose</option>
          {(cards ? cards.paymentProfiles : []).map((c) => <option key={c.customerPaymentProfileId} value={c.customerPaymentProfileId}>{c.payment.creditCard.cardType} {c.payment.creditCard.cardNumber}</option>)}
        </select>
        <button className="btn btn-primary" disabled={busy || !selected} onClick={() => run('card_charge', { orderId, customerProfileId: cards.customerProfileId, customerPaymentProfileId: selected, amount: money(amount || balance) }, `Charge $${money(amount || balance)} to ${pick ? pick.payment.creditCard.cardNumber : 'this card'}?`)}>Charge</button>
        <button className="btn btn-outline" disabled={busy || !selected} onClick={() => run('card_preferred', { customerId, paymentProfileId: selected }, 'Make this the card renewals use first?')}>Preferred</button>
        <button className="btn btn-outline" disabled={busy || !selected} onClick={() => run('card_delete', { customerId, customerProfileId: cards.customerProfileId, customerPaymentProfileId: selected, last4: pick ? pick.payment.creditCard.cardNumber : '', orderId }, 'Delete this card?')}>Delete card</button>
      </div>
      {cards && cards.customerProfileId ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          <input ref={number} aria-label="Card number" className="form-input" style={{ width: 170 }} autoComplete="off" placeholder="Card number" />
          <input ref={expiry} aria-label="Expiry" className="form-input" style={{ width: 70 }} autoComplete="off" placeholder="MM/YY" />
          <input ref={code} type="password" aria-label="Security code" className="form-input" style={{ width: 60 }} autoComplete="off" placeholder="CVV" />
          <button className="btn btn-outline" disabled={busy} onClick={addCard}>Add card</button>
        </div>
      ) : (
        <button className="btn btn-outline" disabled={busy || !capture.length} style={{ marginBottom: 8 }} onClick={() => run('cim_create', { transactionId: capture[0].transactionId, customerId, orderId }, 'Create a CIM profile from this charge?')}>Create CIM</button>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <span className="form-label">Gift on this order</span>
        <input aria-label="Gift code" className="form-input" style={{ width: 170 }} placeholder="Code" value={gift.code} onChange={(e) => setGift({ ...gift, code: e.target.value })} />
        <input aria-label="Gift amount" className="form-input" style={{ width: 80 }} placeholder="Amount" value={gift.amount} onChange={(e) => setGift({ ...gift, amount: e.target.value })} />
        <button className="btn btn-outline" disabled={busy || !gift.code} onClick={() => run('gift_apply', { orderId, giftNumber: gift.code, amount: money(gift.amount) }, `Set ${gift.code} to $${money(gift.amount)} on this order?`)}>Apply gift</button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="form-label">Record, moves no money</span>
        <input aria-label="Reference" className="form-input" style={{ width: 200 }} placeholder="Reference" value={manual.transactionId} onChange={(e) => setManual({ ...manual, transactionId: e.target.value })} />
        <input aria-label="Record amount" className="form-input" style={{ width: 80 }} placeholder="Amount" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} />
        <input aria-label="Last 4" className="form-input" style={{ width: 70 }} placeholder="Last 4" maxLength={4} value={manual.last4} onChange={(e) => setManual({ ...manual, last4: e.target.value })} />
        <select aria-label="Record type" className="form-input" style={{ width: 90 }} value={manual.transactionType} onChange={(e) => setManual({ ...manual, transactionType: e.target.value })}>
          <option>Charge</option>
          <option>Credit</option>
        </select>
        <button className="btn btn-outline" disabled={busy || !manual.transactionId} onClick={() => run('payment_record', { orderId, amount: money(manual.amount), transactionType: manual.transactionType, transactionId: manual.transactionId, transactionStatus: 'Approved', creditCardNumber: manual.last4, expirationDate: 'XXXX' }, 'Record this payment? It does not charge or credit a card.')}>Record</button>
      </div>
      {message && <div role="status" style={{ marginTop: 8, fontWeight: 700 }}>{message}</div>}
    </div>
  );
}